import { useEffect, useRef, useState } from "react";
import { categories, allergens } from "@/data/menu";
import {
  MapPin,
  Phone,
  Clock,
  Instagram,
  Facebook,
  ChevronRight,
  Beer,
  FileDown,
  Loader2,
} from "lucide-react";

const OPENING_NOTE =
  "Pane e focacce fatti in casa con lievito madre. Tutti i prodotti a base di latte sono privi o a basso contenuto di lattosio.";

const ADDRESS = "Via Annibale da Bassano 43/A, 35010 Vigodarzere (PD)";
const PHONE_DISPLAY = "351 553 5876";
const PHONE_TEL = "+393515535876";
const MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Via+Annibale+da+Bassano+43%2FA+35010+Vigodarzere+PD";
const MAPS_EMBED =
  "https://www.google.com/maps?q=Via%20Annibale%20da%20Bassano%2043%2FA%2C%2035010%20Vigodarzere%20PD&output=embed";

const IG_URL = "https://www.instagram.com/brenthaus/";
const FB_URL = "https://www.facebook.com/Brenthaus";
const WA_URL =
  "https://wa.me/393515535876?text=" +
  encodeURIComponent(
    "Ciao BrentHaus! Vorrei prenotare un tavolo. Data: ___ · Ora: ___ · Persone: ___ · Nome: ___"
  );

function WhatsAppIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20.52 3.48A11.82 11.82 0 0 0 12.06 0C5.5 0 .15 5.34.15 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.3-1.66a11.9 11.9 0 0 0 5.76 1.47h.01c6.56 0 11.91-5.34 11.91-11.9a11.82 11.82 0 0 0-3.46-8.43Zm-8.46 18.3h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.22-3.74.98 1-3.65-.24-.37a9.88 9.88 0 0 1-1.51-5.24c0-5.46 4.45-9.9 9.91-9.9 2.64 0 5.13 1.03 7 2.9a9.84 9.84 0 0 1 2.9 7c0 5.46-4.45 9.9-9.91 9.9Zm5.43-7.42c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48a9 9 0 0 1-1.66-2.07c-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.2 5.08 4.49.71.31 1.26.5 1.7.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.12-.27-.2-.57-.34Z" />
    </svg>
  );
}

function SectionKicker({ children }) {
  return <span className="section-kicker">{children}</span>;
}

function MenuItem({ name, desc, price }) {
  return (
    <div
      className="group flex items-start gap-3 py-4 border-b border-[#3d3528]/50 last:border-0 transition-colors"
      data-testid={`menu-item-${name.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline flex-wrap">
          <h4 className="font-display text-xl sm:text-2xl text-bh-cream tracking-wide">
            {name}
          </h4>
          <span className="dotted-leader hidden sm:block" />
          <span className="price sm:hidden ml-auto pl-3">€{price}</span>
        </div>
        {desc ? (
          <p className="mt-1 text-sm sm:text-[15px] text-bh-muted leading-relaxed max-w-xl">
            {desc}
          </p>
        ) : null}
      </div>
      <span className="price hidden sm:inline-block pt-1">€{price}</span>
    </div>
  );
}

function DownloadMenuButton() {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { downloadMenuPdf } = await import("@/lib/menuPdf");
      await downloadMenuPdf({ openingNote: OPENING_NOTE, address: ADDRESS, phone: PHONE_DISPLAY });
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid="download-menu-pdf-btn"
      className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-bh-amber/60 text-bh-amber text-xs font-display tracking-[0.2em] uppercase hover:bg-bh-amber hover:text-[#1a1712] transition-colors disabled:opacity-60"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
      {busy ? "Preparo il PDF…" : "Scarica menu PDF"}
    </button>
  );
}

function MenuCategoryBody({ category }) {
  if (category.message) {
    return (
      <div
        className="relative overflow-hidden rounded-2xl border border-bh-amber/30 bg-[#1f1b15] px-6 py-10 sm:px-10 sm:py-14 text-center"
        data-testid={`menu-message-${category.id}`}
      >
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-bh-amber/10 blur-3xl pointer-events-none" />
        <h3 className="relative font-display text-3xl sm:text-4xl tracking-[0.15em] text-bh-amber uppercase">
          {category.message.title}
        </h3>
        <p className="relative mt-4 text-base sm:text-lg text-bh-cream/90 leading-relaxed max-w-xl mx-auto">
          {category.message.text}
        </p>
      </div>
    );
  }
  if (category.groups) {
    return (
      <div className="space-y-10">
        {category.groups.map((g) => (
          <div key={g.title}>
            <div className="flex items-center gap-3 mb-3">
              <h3 className="font-display text-2xl tracking-[0.15em] text-bh-amber uppercase">
                {g.title}
              </h3>
              <div className="flex-1 h-px bg-[#3d3528]" />
            </div>
            <div>
              {g.items.map((it) => (
                <MenuItem key={it.name} {...it} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div>
      {category.note ? (
        <p className="text-xs uppercase tracking-[0.2em] text-bh-muted mb-3">
          {category.note}
        </p>
      ) : null}
      {category.items.map((it) => (
        <MenuItem key={it.name} {...it} />
      ))}
    </div>
  );
}

export default function BrentHaus() {
  const [activeId, setActiveId] = useState(categories[0].id);
  const activeCategory = categories.find((c) => c.id === activeId);
  const tabRowRef = useRef(null);

  // Ensure active tab is scrolled into view on mobile
  useEffect(() => {
    const row = tabRowRef.current;
    if (!row) return;
    const el = row.querySelector(`[data-tab-id="${activeId}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [activeId]);

  return (
    <div className="relative min-h-screen text-bh-cream" data-testid="brenthaus-app">
      {/* top micro nav */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-[#1a1712]/60 border-b border-[#3d3528]/60">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-14 flex items-center justify-between">
          <a
            href="#hero"
            className="font-display text-xl tracking-[0.2em] text-bh-amber"
            data-testid="nav-brand"
          >
            BRENTHAUS
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm uppercase tracking-[0.25em] text-bh-muted">
            <a href="#menu" className="hover:text-bh-amber transition-colors" data-testid="nav-menu">Menu</a>
            <a href="#orari" className="hover:text-bh-amber transition-colors" data-testid="nav-orari">Orari</a>
            <a href="#contatti" className="hover:text-bh-amber transition-colors" data-testid="nav-contatti">Contatti</a>
          </nav>
          <a
            href={`tel:${PHONE_TEL}`}
            className="inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.2em] text-bh-cream hover:text-bh-amber transition-colors"
            data-testid="nav-phone"
          >
            <Phone size={14} />
            <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
            <span className="sm:hidden">Chiama</span>
          </a>
        </div>
      </header>

      {/* HERO */}
      <section
        id="hero"
        className="relative min-h-[100svh] flex flex-col items-center justify-center px-6 pt-24 pb-16 overflow-hidden"
        data-testid="hero-section"
      >
        {/* ambient glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[140%] h-[120%] opacity-70"
          style={{
            background:
              "radial-gradient(ellipse 50% 40% at 50% 40%, rgba(212,160,23,0.25), transparent 60%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-40"
          style={{
            background:
              "linear-gradient(180deg, transparent, #1a1712 90%)",
          }}
        />

        <div className="relative z-10 text-center fade-up">
          <div className="inline-flex items-center gap-3 text-bh-amber mb-6">
            <Beer size={18} />
            <span className="section-kicker border-none !p-0">Osteria · Birreria · Vigodarzere</span>
          </div>
          <h1
            className="font-display text-bh-cream leading-[0.85] tracking-tight select-none"
            style={{ fontSize: "clamp(4rem, 14vw, 12rem)" }}
            data-testid="hero-title"
          >
            BRENT<span className="text-bh-amber">HAUS</span>
          </h1>
          <p
            className="mt-6 max-w-2xl mx-auto text-lg sm:text-xl md:text-2xl text-bh-muted leading-snug"
            data-testid="hero-tagline"
          >
            Birra artigianale, cucina di territorio,{" "}
            <span className="text-bh-cream italic">buona compagnia.</span>
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href="#menu"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-bh-amber text-[#1a1712] font-display tracking-[0.15em] text-base hover:bg-bh-amberSoft transition-colors"
              data-testid="hero-cta-menu"
            >
              Sfoglia il menu
              <ChevronRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] text-[#0b1d14] font-display tracking-[0.15em] text-base hover:bg-[#2ee070] transition-colors shadow-[0_0_0_1px_rgba(37,211,102,0.3),0_8px_24px_-8px_rgba(37,211,102,0.6)]"
              data-testid="hero-cta-whatsapp"
            >
              <WhatsAppIcon size={16} /> Prenota via WhatsApp
            </a>
            <a
              href={`tel:${PHONE_TEL}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#3d3528] text-bh-cream font-display tracking-[0.15em] text-base hover:border-bh-amber hover:text-bh-amber transition-colors"
              data-testid="hero-cta-call"
            >
              <Phone size={14} /> Chiama
            </a>
          </div>
        </div>

        {/* scroll hint */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-bh-muted text-xs uppercase tracking-[0.3em] opacity-70">
          ↓ Scorri
        </div>
      </section>

      {/* MENU */}
      <section
        id="menu"
        className="relative py-20 sm:py-28 border-t border-[#3d3528]/60"
        data-testid="menu-section"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="flex flex-col items-start gap-4 mb-8">
            <SectionKicker>La Carta</SectionKicker>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight text-bh-cream">
              Il nostro menu
            </h2>
            <p
              className="max-w-2xl text-bh-muted italic text-sm sm:text-base leading-relaxed border-l-2 border-bh-amber pl-4 mt-2"
              data-testid="menu-opening-note"
            >
              {OPENING_NOTE}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex items-end gap-4 border-b border-[#3d3528] mb-10">
            <div
              ref={tabRowRef}
              className="no-scrollbar overflow-x-auto flex-1 min-w-0"
              role="tablist"
              aria-label="Categorie menu"
              data-testid="menu-tabs"
            >
              <div className="flex gap-7 min-w-max px-1">
                {categories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    role="tab"
                    aria-selected={activeId === c.id}
                    data-active={activeId === c.id}
                    data-tab-id={c.id}
                    data-testid={`menu-tab-${c.id}`}
                    onClick={() => setActiveId(c.id)}
                    className="tab-btn"
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="hidden sm:block pb-3">
              <DownloadMenuButton />
            </div>
          </div>
          <div className="sm:hidden -mt-6 mb-8">
            <DownloadMenuButton />
          </div>

          {/* Active category */}
          <div
            key={activeCategory.id}
            className="fade-up bg-[#231f18]/40 border border-[#3d3528]/70 rounded-2xl p-5 sm:p-8 md:p-10"
            data-testid={`menu-panel-${activeCategory.id}`}
          >
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
              <h3 className="font-display text-3xl sm:text-4xl tracking-wide text-bh-amber uppercase">
                {activeCategory.label}
              </h3>
              {!activeCategory.message && (
                <span className="text-xs uppercase tracking-[0.25em] text-bh-muted">
                  Prezzi in €
                </span>
              )}
            </div>
            <MenuCategoryBody category={activeCategory} />
          </div>

          {/* Allergen legend */}
          <details
            className="mt-8 group rounded-xl border border-[#3d3528]/70 bg-[#231f18]/30 overflow-hidden"
            data-testid="allergens-legend"
          >
            <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4 hover:bg-[#231f18]/60 transition-colors">
              <span className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-bh-amber/50 text-bh-amber font-display text-sm">
                  ⚠
                </span>
                <span className="font-display tracking-[0.2em] text-sm sm:text-base text-bh-cream uppercase">
                  Legenda Allergeni
                </span>
              </span>
              <span className="text-bh-muted text-xs uppercase tracking-[0.2em] group-open:rotate-180 transition-transform">
                ⌄
              </span>
            </summary>
            <div className="px-5 pb-5 pt-1">
              <p className="text-xs text-bh-muted mb-3 italic">
                Sostanze o prodotti che provocano allergie o intolleranze · Allegato II Reg. UE 1169/2011
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                {allergens.map((a) => (
                  <li
                    key={a.n}
                    className="flex items-baseline gap-2 text-bh-cream/90"
                    data-testid={`allergen-${a.n}`}
                  >
                    <span className="font-display text-bh-amber w-7 shrink-0">
                      ({a.n})
                    </span>
                    <span>{a.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </details>
        </div>
      </section>

      {/* ORARI */}
      <section
        id="orari"
        className="relative py-20 sm:py-24"
        data-testid="orari-section"
      >
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <div className="text-center mb-10">
            <SectionKicker>Quando siamo aperti</SectionKicker>
            <h2 className="font-display text-5xl sm:text-6xl tracking-tight text-bh-cream mt-3">
              Orari
            </h2>
          </div>

          <div
            className="relative rounded-2xl border border-[#3d3528] bg-[#231f18]/60 p-8 md:p-12 overflow-hidden"
            data-testid="orari-card"
          >
            <div
              aria-hidden
              className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20"
              style={{
                background:
                  "radial-gradient(circle, rgba(212,160,23,0.7), transparent 60%)",
              }}
            />
            <div className="relative z-10 flex items-center gap-3 text-bh-amber mb-6">
              <Clock size={18} />
              <span className="font-display tracking-[0.2em] text-sm">
                APERTURA SETTIMANALE
              </span>
            </div>

            <ul className="relative z-10 divide-y divide-[#3d3528]/70">
              <li className="flex items-center justify-between py-4" data-testid="orari-lun-ven">
                <span className="font-display text-2xl sm:text-3xl tracking-wide">
                  Lunedì – Venerdì
                </span>
                <span className="price text-xl sm:text-2xl">18:00 – 01:00</span>
              </li>
              <li className="flex items-center justify-between py-4" data-testid="orari-sab">
                <span className="font-display text-2xl sm:text-3xl tracking-wide">
                  Sabato
                </span>
                <span className="price text-xl sm:text-2xl">18:00 – 01:00</span>
              </li>
              <li className="flex items-center justify-between py-4" data-testid="orari-dom">
                <span className="font-display text-2xl sm:text-3xl tracking-wide">
                  Domenica
                </span>
                <span className="text-bh-muted font-display text-xl sm:text-2xl tracking-wide">
                  Chiuso
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CONTATTI & MAPPA */}
      <section
        id="contatti"
        className="relative py-20 sm:py-28 border-t border-[#3d3528]/60"
        data-testid="contatti-section"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="mb-12">
            <SectionKicker>Dove trovarci</SectionKicker>
            <h2 className="font-display text-5xl sm:text-6xl tracking-tight text-bh-cream mt-3">
              Contatti & Mappa
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
            {/* Info */}
            <div className="flex flex-col gap-6">
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-2xl border border-[#3d3528] bg-[#231f18]/50 p-6 hover:border-bh-amber transition-colors"
                data-testid="contatti-address-link"
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-full border border-bh-amber/50 flex items-center justify-center text-bh-amber">
                    <MapPin size={18} />
                  </div>
                  <div>
                    <div className="font-display tracking-[0.2em] text-sm text-bh-muted">
                      INDIRIZZO
                    </div>
                    <div className="mt-1 font-display text-2xl sm:text-3xl text-bh-cream group-hover:text-bh-amber transition-colors tracking-wide leading-tight">
                      {ADDRESS}
                    </div>
                  </div>
                </div>
              </a>

              <a
                href={`tel:${PHONE_TEL}`}
                className="group block rounded-2xl border border-[#3d3528] bg-[#231f18]/50 p-6 hover:border-bh-amber transition-colors"
                data-testid="contatti-phone-link"
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-full border border-bh-amber/50 flex items-center justify-center text-bh-amber">
                    <Phone size={18} />
                  </div>
                  <div>
                    <div className="font-display tracking-[0.2em] text-sm text-bh-muted">
                      TELEFONO
                    </div>
                    <div className="mt-1 font-display text-2xl sm:text-3xl text-bh-cream group-hover:text-bh-amber transition-colors tracking-wide">
                      {PHONE_DISPLAY}
                    </div>
                  </div>
                </div>
              </a>

              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-2xl border border-[#25D366]/40 bg-[#25D366]/5 p-6 hover:border-[#25D366] transition-colors"
                data-testid="contatti-whatsapp-link"
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-full border border-[#25D366]/60 flex items-center justify-center text-[#25D366]">
                    <WhatsAppIcon size={18} />
                  </div>
                  <div>
                    <div className="font-display tracking-[0.2em] text-sm text-bh-muted">
                      WHATSAPP · PRENOTA IN UN TAP
                    </div>
                    <div className="mt-1 font-display text-2xl sm:text-3xl text-bh-cream group-hover:text-[#25D366] transition-colors tracking-wide">
                      Scrivici su WhatsApp
                    </div>
                  </div>
                </div>
              </a>

              <div className="rounded-2xl border border-[#3d3528] bg-[#231f18]/50 p-6" data-testid="contatti-orari-recap">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-full border border-bh-amber/50 flex items-center justify-center text-bh-amber">
                    <Clock size={18} />
                  </div>
                  <div>
                    <div className="font-display tracking-[0.2em] text-sm text-bh-muted">
                      APERTI
                    </div>
                    <div className="mt-1 font-display text-xl sm:text-2xl text-bh-cream tracking-wide leading-snug">
                      Lun – Sab · 18:00 – 01:00
                    </div>
                    <div className="text-bh-muted text-sm mt-1">Domenica chiuso</div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-2">
                <a
                  href={IG_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram BrentHaus"
                  className="w-11 h-11 rounded-full border border-[#3d3528] flex items-center justify-center text-bh-cream hover:text-bh-amber hover:border-bh-amber transition-colors"
                  data-testid="contatti-instagram"
                >
                  <Instagram size={18} />
                </a>
                <a
                  href={FB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook BrentHaus"
                  className="w-11 h-11 rounded-full border border-[#3d3528] flex items-center justify-center text-bh-cream hover:text-bh-amber hover:border-bh-amber transition-colors"
                  data-testid="contatti-facebook"
                >
                  <Facebook size={18} />
                </a>
                <span className="text-bh-muted text-sm uppercase tracking-[0.2em] ml-2">
                  Seguici
                </span>
              </div>
            </div>

            {/* Map */}
            <div
              className="relative rounded-2xl overflow-hidden border border-[#3d3528] min-h-[380px] lg:min-h-full"
              data-testid="contatti-map"
            >
              <iframe
                title="Mappa BrentHaus"
                src={MAPS_EMBED}
                className="map-dark w-full h-full min-h-[380px]"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-bh-amber text-[#1a1712] text-xs font-display tracking-[0.2em] hover:bg-bh-amberSoft transition-colors"
                data-testid="contatti-map-open"
              >
                Apri in Maps <ChevronRight size={12} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer
        className="relative border-t border-[#3d3528]/60 bg-[#130f0b]"
        data-testid="footer"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-12 grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
          <div>
            <div className="font-display text-3xl tracking-[0.2em] text-bh-amber">
              BRENTHAUS
            </div>
            <p className="mt-3 text-sm text-bh-muted leading-relaxed italic">
              Birra artigianale, cucina di territorio, buona compagnia.
            </p>
          </div>

          <div>
            <div className="font-display tracking-[0.25em] text-xs text-bh-muted mb-2">
              DOVE
            </div>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block font-display text-lg text-bh-cream hover:text-bh-amber transition-colors leading-snug"
              data-testid="footer-address"
            >
              {ADDRESS}
            </a>
            <a
              href={`tel:${PHONE_TEL}`}
              className="mt-2 inline-block text-bh-muted hover:text-bh-amber transition-colors"
              data-testid="footer-phone"
            >
              Tel. {PHONE_DISPLAY}
            </a>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366] text-[#0b1d14] font-display tracking-[0.15em] text-sm hover:bg-[#2ee070] transition-colors"
              data-testid="footer-whatsapp"
            >
              <WhatsAppIcon size={14} /> Prenota su WhatsApp
            </a>
          </div>

          <div>
            <div className="font-display tracking-[0.25em] text-xs text-bh-muted mb-2">
              ORARI
            </div>
            <div className="font-display text-lg text-bh-cream leading-snug">
              Lun – Sab · 18:00 – 01:00
            </div>
            <div className="text-bh-muted text-sm mt-1">Domenica chiuso</div>

            <div className="mt-5 flex items-center gap-3">
              <a
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-9 h-9 rounded-full border border-[#3d3528] flex items-center justify-center text-bh-cream hover:text-bh-amber hover:border-bh-amber transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href={FB_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-9 h-9 rounded-full border border-[#3d3528] flex items-center justify-center text-bh-cream hover:text-bh-amber hover:border-bh-amber transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook size={16} />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-[#3d3528]/50">
          <div className="max-w-7xl mx-auto px-5 md:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-bh-muted uppercase tracking-[0.2em]">
            <span>© {new Date().getFullYear()} BrentHaus</span>
            <span>Osteria · Birreria · Vigodarzere (PD)</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
